import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  MenuSeparator,
  MenuItem as RawMenuItem,
  Tooltip,
} from '@enechain/ecloud-designsystem'

import type { IntradayBulkWithdraw } from '~/pages/IntradayBidding/hooks/useIntradayBulkWithdraw'

/**
 * chakra v3 の `MenuItemProps` は本プロジェクトの型解決で collapse するため、 v3 本来の
 * prop シグネチャへ cast して復元する (`BidCreateTab` / `MasterTableActionBar` と同じ)。
 */
const MenuItem = RawMenuItem as React.FC<
  HTMLChakraProps<'div'> & {
    value: string
    closeOnSelect?: boolean
    onSelect?: () => void
  }
>

/** ⋯メニューでこの項目を見分ける値 (強調中かの判定にも使う)。 */
export const INTRADAY_BULK_WITHDRAW_MENU_VALUE = 'bulk-withdraw'

type Props = {
  bulkWithdraw: Pick<IntradayBulkWithdraw, 'selectable' | 'tooltip' | 'open'>
  /** メニューで強調中か (ホバー・矢印キーのどちらでも true)。 強調中にツールチップを出す。 */
  highlighted: boolean
}

/**
 * 入札一覧の操作行の ⋯メニューの「入札中の札を一括で板取消」 (AC-BIN-112、 design
 * `intraday.html` の `[data-intraday-bulk-cancel]`)。 他の項目と区切り線で分ける。
 *
 * 選べない時は `disabled` でなく `aria-disabled` にする。 ark-ui (zag) の menu は `disabled` の
 * 項目を矢印キーで飛ばし、 キーボードでは理由のツールチップに届かないため
 * (`MasterTableActionBar` の RichMenuItem と同じ。 UI 辞書 action-reason 区分 2)。
 * ツールチップは項目の内側に置く (項目を包むと trigger の属性が項目に上書きされ、 menu が
 * 矢印キーで項目を見つけられなくなる)。
 *
 * [Des確認] design は 2 行の rich item (説明行「絞り込み条件に合う札を JEPX から取り消す」) +
 * アイコンだが、 UI 辞書 `design/ui-feasibility/dictionary/components.md` は 2 行の item を
 * split button の ▾ の中に限るので、 同じ ⋯メニューの他の項目と同じ 1 行の項目にする
 * (対象の範囲は押せる時のツールチップの件数で示す)。
 */
export const IntradayBulkWithdrawMenuItem = ({
  bulkWithdraw,
  highlighted,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { selectable, tooltip, open } = bulkWithdraw
  return (
    <>
      {/* 下書きを消す操作・設定画面への導線と分ける (design の区切り線)。 */}
      <MenuSeparator />
      <MenuItem
        aria-disabled={selectable ? undefined : true}
        closeOnSelect={selectable}
        onSelect={selectable ? open : undefined}
        value={INTRADAY_BULK_WITHDRAW_MENU_VALUE}
      >
        <Tooltip
          content={tooltip ?? ''}
          isHidden={tooltip === undefined || tooltip === ''}
          open={highlighted}
        >
          <Box as="span">
            {t('page:biddingIntraday.bulkWithdraw.menu.title')}
          </Box>
        </Tooltip>
      </MenuItem>
    </>
  )
}
